import React, { useEffect, useMemo, useState } from "react";
import { getSkill } from "../../../lib/inventorySkills";
import { useSeasonStats } from "../../../lib/seasonStats";
import { computeDefense, dfList, useDefense } from "../../../lib/defense";
import { DefenseStatsPanel } from "./DefenseStatsPanel";
import { DefenseOutcomes } from "./DefenseOutcomes";
import { PlanLink } from "../PlanLink";
import { ItemRow } from "../ItemRow";

export const EMPTY_DEF = { outcome_tags: [], flags: [], checkpoints: [], limiting_factors: [] };
const FROM = { "PHY-AT-02": "Physical", "CHR-MO-03": "Character DNA" };

// Defense = same 4 sections as Finishing: stats panel → rate the 7 interactions (+2 linked) → outcome tags (3 rows) → the plan.
export function DefenseCategory({ playerId, items, byId, defense, onDefenseChange, rowProps, readOnly }) {
  const lib = useDefense();
  const { records } = useSeasonStats(playerId);
  const df = defense || EMPTY_DEF;
  const [season, setSeason] = useState(df.season || null);
  useEffect(() => { if (!season && records.length) setSeason(df.season && records.some(r => r.season === df.season) ? df.season : records[0].season); }, [records, season, df.season]);
  const rec = records.find(r => r.season === season);
  const result = useMemo(() => computeDefense(rec?.stats), [rec]);
  useEffect(() => {
    if (readOnly || !season) return;
    const flags = result?.flags || [];
    if (season !== df.season || JSON.stringify(flags) !== JSON.stringify(df.flags || [])) onDefenseChange(s => ({ ...(s || EMPTY_DEF), season, flags }));
  }, [season, result]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!lib) return <div className="text-[10px] text-[#757575]">Loading Defense…</div>;
  const order = dfList(lib, "Interaction").map(r => r.id);
  const ordered = [...items].sort((a, b) => (order.indexOf(a.id) + 1 || 99) - (order.indexOf(b.id) + 1 || 99));
  const linked = dfList(lib, "Linked Skill").map(r => getSkill(r.id)).filter(s => s && s.status === "active");
  const look = new Set([...(result?.flags || []).flatMap(f => dfList(lib, "Stat Flag").find(x => x.id === f)?.points_to || []),
    ...(result?.types || []).filter(t => t.pppTier === "Struggling").map(t => dfList(lib, "Play Type Defended").find(p => p.id === t.pppId)?.points_to).filter(Boolean)]);
  const row = (s, fromCat) => (
    <div key={s.id} className="relative" data-testid={`df-skill-${s.id}`}>
      {(look.has(s.id) || fromCat) && <div className="absolute right-3 -top-2 z-10 flex items-center gap-1.5">
        {look.has(s.id) && <span className="px-1.5 py-0.5 bg-[#f59e0b] text-[#121212] text-[8px] uppercase tracking-[0.2em] font-bold" data-testid={`df-look-${s.id}`}>Look at this</span>}
        {fromCat && <span className="px-1.5 py-0.5 border border-white/20 bg-[#1c1c1c] text-[#a0a0a0] text-[8px] uppercase tracking-[0.2em]" data-testid={`df-linked-${s.id}`}>From {fromCat}</span>}</div>}
      <ItemRow skill={s} rating={byId[s.id]} readOnly={readOnly} {...rowProps(s)} />
    </div>
  );
  return (
    <div className="space-y-3" data-testid="df-category">
      <DefenseStatsPanel playerId={playerId} lib={lib} records={records} season={season} setSeason={setSeason} result={result} />
      <div className="border border-white/10 bg-[#141414] p-4 space-y-2" data-testid="df-ratings">
        <div className="flex items-center gap-3 flex-wrap"><span className="text-[9px] font-mono text-[var(--org-accent)]">2</span><div className="text-[10px] uppercase tracking-[0.35em] font-bold text-white">Rate the interactions</div><div className="text-[10px] text-[#757575]">Anchor / Good / Rate Limiter · the stats inform, the coach rates</div></div>
        <div className="space-y-3 pt-1">{ordered.map(s => row(s, null))}{linked.map(s => row(s, FROM[s.id] || s.category))}</div>
      </div>
      <DefenseOutcomes lib={lib} df={df} byId={byId} readOnly={readOnly} onChange={onDefenseChange} />
      <PlanLink playerId={playerId} sectionKey="defense" />
    </div>
  );
}
